import React from 'react';
import { formatDateTimeCompact } from '../../utils/dateUtils';

// ─── DS class mapping ────────────────────────────────────────────────
// Section container : Compact View Spacing  → bg-gray-50 rounded-xl p-4
// Section heading   : card-interior label   → text-xs font-semibold text-gray-500 uppercase tracking-wide
// Body text         : Typography/body       → text-sm font-medium text-gray-900
// Secondary text    : Typography/small      → text-xs text-gray-500
// Timestamp         : right-aligned detail  → text-xs text-gray-400 whitespace-nowrap
// Dot colors        : Status Taxonomy tiers → blue/green/orange/red/gray
// Pending animation : Motion & States       → animate-pulse ring-2 ring-orange-200
// Transitions       : Motion & States       → transition-colors duration-200
// ─────────────────────────────────────────────────────────────────────

const DOT_COLORS = {
  blue: 'bg-blue-500',
  green: 'bg-green-500',
  orange: 'bg-orange-500',
  red: 'bg-red-500',
  gray: 'bg-gray-400',
};

const ROLE_LABELS = {
  reporting_officer: 'Reporting Officer',
  trade_leader: 'Trade Leader',
  deputy_hod: 'Deputy HOD',
  department_head: 'Department Head',
  line_manager: 'Line Manager',
  hr_manager: 'HR Manager',
  finance_manager: 'Finance Manager',
};

const formatRole = (role) => {
  if (!role) return 'Approver';
  return ROLE_LABELS[role] || role.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

const TimelineEntry = ({ color, label, subtitle, timestamp, isPending = false }) => (
  <div className="relative">
    <div className="absolute -left-7 top-1.5">
      <div
        className={`w-2.5 h-2.5 rounded-full transition-colors duration-200 ${
          DOT_COLORS[color] || DOT_COLORS.orange
        } ${isPending ? 'animate-pulse ring-2 ring-orange-200' : ''}`}
      />
    </div>
    <div className="flex items-start justify-between gap-2 min-w-0">
      <div className="min-w-0">
        <p className="text-sm font-medium text-gray-900">{label}</p>
        {subtitle && (
          <p className="text-xs text-gray-500 mt-0.5 break-words">{subtitle}</p>
        )}
      </div>
      {timestamp && (
        <span className="text-xs text-gray-400 whitespace-nowrap flex-shrink-0">
          {formatDateTimeCompact(timestamp)}
        </span>
      )}
    </div>
  </div>
);

/**
 * Shared timeline component that renders the full approval lifecycle
 * of a leave application from a single `leave` prop.
 *
 * Events rendered (when data exists):
 *   Submitted → Approval levels → Final approval/rejection →
 *   Flight ticket submitted → HR verification → Cancellation → Pending
 *
 * Consumers:
 *   - WorkerLeaveModal  Step4Complete  (Annual Leave completion)
 *   - WorkerLeaveModal  Step5Complete  (Home / Urgent Leave completion)
 *   - Leave.js          staff detail modal
 *   - LeaveApplicationDetailModal (admin)
 */
const ApprovalTimeline = ({ leave }) => {
  if (!leave) return null;

  const entries = [];
  const status = leave.leave_status;
  const history = leave.approval_history || [];
  // Sick leave is approved by HR verifying the MC, not by RO/Trade Leader.
  const leaveTypeName = (leave.leave_type?.leave_type_name || leave.leave_type_name || '').toLowerCase();
  const isSickLeave = leaveTypeName === 'outpatient sick leave' || leaveTypeName === 'hospitalisation leave';
  // Prefer the live workflow hierarchy for "is this multi-level?" — stale
  // history rows from a prior workflow (e.g. Logistics realigned to RO-only)
  // must not invent an L2 Trade Leader step HR never required.
  const hierarchy = leave.approval_hierarchy || [];
  const hierarchyLevels = new Set(hierarchy.map((h) => h.level).filter((l) => l != null));

  const actionEntries = history.filter((h) => {
    if (!(h.approved_at || h.action === 'escalated' || h.action === 'auto_advanced')) {
      return false;
    }
    // Drop approval/skip rows whose level is outside the current workflow
    if (
      hierarchyLevels.size > 0
      && h.level != null
      && (h.action === 'approved' || h.action === 'auto_skipped')
      && !hierarchyLevels.has(h.level)
    ) {
      return false;
    }
    return true;
  });
  const sorted = [...actionEntries].sort((a, b) => (a.level || 0) - (b.level || 0));

  const isMultiLevel =
    hierarchy.length > 1
    || (hierarchy.length === 0 && sorted.length > 1);

  // ── 1. Application Submitted ───────────────────────────────────────
  const submittedAt = leave.leave_applied_at || leave.submitted_at || leave.created_at;
  if (submittedAt) {
    entries.push({ color: 'orange', label: 'Application Submitted', timestamp: submittedAt, order: 0 });
  }

  // ── 2. Approval history entries (level-by-level) ───────────────────
  sorted.forEach((entry) => {
    const roleLabel = formatRole(entry.role);
    const levelPrefix = isMultiLevel ? `L${entry.level}: ` : '';
    const approverName = entry.approver_name || '';
    const approverId = entry.approver_id || '';
    const approverLabel = [approverName, approverId].filter(Boolean).join(' · ');

    switch (entry.action) {
      case 'auto_skipped': {
        // Backend stores the real skip reason. Only the "same person covers
        // two roles" path should read as Auto-approved; missing-approver /
        // workflow-realignment skips are "Skipped", not a second approval.
        const reason = (entry.reason || '').trim();
        const isSameApprover = /same approver/i.test(reason);
        let subtitle;
        if (isSameApprover && approverLabel) {
          subtitle = `${approverLabel} (same approver as previous level)`;
        } else if (reason) {
          subtitle = reason;
        } else if (approverLabel) {
          subtitle = `${approverLabel} (same approver as previous level)`;
        } else {
          subtitle = 'Level not required';
        }
        entries.push({
          color: 'green',
          label: `${levelPrefix}${isSameApprover || approverLabel ? 'Auto-approved' : 'Skipped'} — ${roleLabel}`,
          subtitle,
          timestamp: entry.approved_at,
          order: entry.level,
        });
        break;
      }

      case 'rejected':
        entries.push({
          color: 'red',
          label: `${levelPrefix}Rejected — ${roleLabel}`,
          subtitle: [approverLabel, entry.reason && `Reason: ${entry.reason}`].filter(Boolean).join(' · '),
          timestamp: entry.approved_at,
          order: entry.level,
        });
        break;

      case 'cancelled':
        entries.push({
          color: 'gray',
          label: entry.admin_override ? 'Cancelled (Admin Override)' : 'Cancelled',
          subtitle: [approverLabel, entry.reason && `Reason: ${entry.reason}`].filter(Boolean).join(' · '),
          timestamp: entry.approved_at,
          order: entry.level,
        });
        break;

      case 'auto_advanced': {
        const advOriginalName = entry.original_approver_name || '';
        const advOriginalId = entry.original_approver_id || '';
        const advOriginalLabel = [advOriginalName, advOriginalId].filter(Boolean).join(' · ');
        entries.push({
          color: 'gray',
          label: `${levelPrefix}No response — advanced`,
          subtitle: advOriginalLabel || entry.reason || '',
          timestamp: entry.timestamp || entry.approved_at,
          order: entry.level,
        });
        break;
      }

      case 'escalated': {
        const originalName = entry.original_approver_name || '';
        const originalId = entry.original_approver_id || '';
        const originalLabel = [originalName, originalId].filter(Boolean).join(' · ');
        const escName = entry.escalation_approver_name || '';
        const escRole = ROLE_LABELS[entry.escalation_role] || entry.escalation_role || '';
        const escLabel = [escName, escRole].filter(Boolean).join(' · ');
        entries.push({
          color: 'red',
          label: `${levelPrefix}Escalated${escLabel ? ` to ${escLabel}` : ''}`,
          subtitle: originalLabel ? `No response from ${originalLabel}` : entry.reason || '',
          timestamp: entry.timestamp || entry.approved_at,
          order: entry.level + 0.5,
        });
        break;
      }

      default:
        entries.push({
          color: 'green',
          label: `${levelPrefix}Approved — ${roleLabel}`,
          subtitle: approverLabel,
          timestamp: entry.approved_at,
          order: entry.level,
        });
    }
  });

  // ── 3. Final approval / auto-approval (only when history is empty) ─
  const hasLevelApprovals = sorted.some(h => h.action === 'approved' || h.action === 'auto_skipped');
  if (!hasLevelApprovals && (status === 'approved' || status === 'auto_approved') && leave.leave_approved_at) {
    entries.push({
      color: 'green',
      label: status === 'auto_approved' ? 'Auto-Approved'
        : isSickLeave ? 'Verified by HR'
        : 'Approved',
      timestamp: leave.leave_approved_at,
      order: 100,
    });
  }

  // ── 4. Top-level rejection (not already in history) ────────────────
  if (status === 'rejected' && !sorted.some(h => h.action === 'rejected')) {
    entries.push({
      color: 'red',
      label: 'Rejected',
      subtitle: leave.leave_rejection_reason || '',
      timestamp: leave.leave_approved_at || leave.leave_updated_at,
      order: 100,
    });
  }

  // ── 5. Flight ticket submitted ─────────────────────────────────────
  if (leave.flight_details_submitted_at) {
    entries.push({
      color: 'orange',
      label: 'Flight Ticket Submitted',
      timestamp: leave.flight_details_submitted_at,
      order: 150,
    });
  }

  // ── 6. HR verification result ──────────────────────────────────────
  // API returns: { status, approved_at, approved_by_staff_id, rejection_reason }
  const verif = leave.flight_ticket_verification;
  if (verif?.status === 'approved') {
    const verifName = verif.approved_by_name || '';
    const verifId = verif.approved_by_staff_id || '';
    entries.push({
      color: 'green',
      label: 'Flight Ticket Verified',
      subtitle: [verifName, verifId].filter(Boolean).join(' · '),
      timestamp: verif.approved_at,
      order: 160,
    });
  } else if (verif?.status === 'rejected') {
    entries.push({
      color: 'red',
      label: 'Flight Ticket Rejected',
      subtitle: verif.rejection_reason || '',
      timestamp: verif.approved_at,
      order: 160,
    });
  }

  // ── 7. Top-level cancellation (not already in history) ─────────────
  if (status === 'cancelled' && !sorted.some(h => h.action === 'cancelled')) {
    entries.push({
      color: 'gray',
      label: leave.admin_override ? 'Cancelled (Admin Override)' : 'Cancelled',
      subtitle: [
        leave.cancelled_by_name && `By: ${leave.cancelled_by_name}`,
        leave.cancellation_reason && `Reason: ${leave.cancellation_reason}`,
      ].filter(Boolean).join(' · '),
      timestamp: leave.cancelled_at,
      order: 200,
    });
  }

  // ── 8. Pending indicator ───────────────────────────────────────────
  if (status === 'pending' || status === 'submitted') {
    const pendingLevel = leave.current_approval_level || sorted.length + 1;
    entries.push({
      color: 'orange',
      label: isSickLeave ? 'Pending HR Verification'
        : isMultiLevel ? `Pending L${pendingLevel}`
        : 'Pending Approval',
      subtitle: isSickLeave ? '' : (leave.current_approver?.name || ''),
      isPending: true,
      order: 999,
    });
  }

  entries.sort((a, b) => a.order - b.order);

  if (entries.length === 0) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Timeline</h4>
        <p className="text-sm text-gray-400 italic">Timeline information not available</p>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Timeline</h4>
      <div className="relative pl-7 space-y-3">
        {/* Vertical line — centered through dot centers.
            Dot: -left-7 (0px from container padding) + w-2.5/2 = center at 5px.
            Line: left-1 (4px) + w-0.5/2 (1px) = center at 5px. Aligned. */}
        <div className="absolute left-1 top-0 bottom-0 w-0.5 bg-gray-200 rounded-full" />
        {entries.map((entry, idx) => (
          <TimelineEntry
            key={idx}
            color={entry.color}
            label={entry.label}
            subtitle={entry.subtitle}
            timestamp={entry.timestamp}
            isPending={entry.isPending}
          />
        ))}
      </div>
    </div>
  );
};

export { TimelineEntry, DOT_COLORS, ROLE_LABELS, formatRole };
export default ApprovalTimeline;
